import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { downloadSampleExcelTemplate } from '../services/jobService';

const TEMPLATE_FIELDS = [
  { field: 'ref_no', required: true, description: 'Reference number shown in the PDF and email.', example: 'REF001' },
  { field: 'name', required: true, description: 'Recipient name used in the greeting.', example: 'John Doe' },
  { field: 'email', required: true, description: 'Recipient email address.', example: 'john@example.com' },
  { field: 'company_name', required: true, description: 'Company or organization name.', example: 'John Corp' },
  { field: 'address_line1', required: true, description: 'First line of the address.', example: '123 Main Street' },
  { field: 'address_line2', required: true, description: 'Second line of the address.', example: 'Mumbai' },
  { field: 'email_subject', required: false, description: 'Optional custom subject suffix for this row.', example: 'License Certificate' },
  { field: 'custom_message', required: false, description: 'Optional custom message appended to the email body.', example: 'Please review the attached certificate.' },
];

const FileUpload = ({ onFileUpload, isLoading, dailyLimit }) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onFileUpload(acceptedFiles[0]);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: isLoading || dailyLimit.remaining_emails === 0,
  });

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadSampleExcelTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_email_sample_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download sample template:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 font-semibold">Daily email limit is 100</p>
        <p className="text-blue-600 text-sm">
          Today's usage: {dailyLimit.emails_sent} / {dailyLimit.emails_sent + dailyLimit.remaining_emails}
        </p>
        <p className="text-blue-600 text-sm">
          Remaining emails: {dailyLimit.remaining_emails}
        </p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Sample Excel sheet</h3>
            <p className="text-sm text-gray-600">
              Download the sample file first, then fill the required columns below.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Download sample Excel sheet
          </button>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${isLoading || dailyLimit.remaining_emails === 0 ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isLoading ? (
          <div className="space-y-2">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600">Uploading file...</p>
          </div>
        ) : (
          <div className="space-y-2">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>

            {isDragActive ? (
              <p className="text-blue-600">Drop the Excel file here...</p>
            ) : (
              <div>
                <p className="text-gray-600">Drag and drop an Excel file here, or click to select</p>
                <p className="text-gray-400 text-sm mt-1">Supported formats: .xlsx, .xls</p>
                <p className="text-gray-400 text-sm">Maximum 100 rows per file</p>
              </div>
            )}
          </div>
        )}
      </div>

      {dailyLimit.remaining_emails === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Uploading is blocked for today.</p>
          <p className="mt-1 text-sm text-red-700">
            {dailyLimit.warning_message || 'Daily email limit has been reached. Please do not try to upload more files today, because repeated sending attempts can affect sender reputation and may lead to email blacklisting.'}
          </p>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-700 mb-2">Required Excel Columns:</h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {TEMPLATE_FIELDS.filter((item) => item.required).map((item) => (
            <span
              key={item.field}
              className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
            >
              {item.field}
            </span>
          ))}
        </div>

        <h3 className="font-semibold text-gray-700 mb-2">Optional Excel Columns:</h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {TEMPLATE_FIELDS.filter((item) => !item.required).map((item) => (
            <span
              key={item.field}
              className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700"
            >
              {item.field}
            </span>
          ))}
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-3 py-2 font-semibold">Field</th>
                <th className="px-3 py-2 font-semibold">Required</th>
                <th className="px-3 py-2 font-semibold">What it is</th>
                <th className="px-3 py-2 font-semibold">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-600">
              {TEMPLATE_FIELDS.map((item) => (
                <tr key={item.field}>
                  <td className="px-3 py-2 font-mono text-xs sm:text-sm text-gray-900">{item.field}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${item.required ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item.required ? 'Required' : 'Optional'}
                    </span>
                  </td>
                  <td className="px-3 py-2">{item.description}</td>
                  <td className="px-3 py-2 font-mono text-xs sm:text-sm text-gray-700">{item.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          The uploaded file is used to generate the PDF, attach it to an HTML email, and send each email with a 7-second delay.
        </p>
      </div>
    </div>
  );
};

export default FileUpload;
